import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
} from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useState } from 'react';

import { graphRefToString, useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

export const ProposalCheckRerunButtonWorkflowFragment = gql`
  fragment ProposalCheckRerunButtonWorkflowFragment on CheckWorkflow {
    id
    proposalRevision {
      id
    }
  }
`;

export const reRunCheckForProposalRevisionMutation = gql<
  GraphQLTypes.ReRunCheckForProposalRevisionMutation,
  GraphQLTypes.ReRunCheckForProposalRevisionMutationVariables
>`
  mutation ReRunCheckForProposalRevisionMutation(
    $input: ReRunCheckForRevisionInput!
    $variantRef: ID!
    $filter: CheckFilterInput
  ) {
    proposalByVariantRef(variantRef: $variantRef) {
      ... on NotFoundError {
        message
      }
      ... on PermissionError {
        message
      }
      ... on ValidationError {
        message
      }
      ... on ProposalMutation {
        reRunCheckForRevision(input: $input) {
          __typename
          ... on NotFoundError {
            message
          }
          ... on PermissionError {
            message
          }
          ... on Proposal {
            id
            # Attempting to fetch the latest workflow id after this mutation runs
            backingVariant {
              id
              graph {
                id
                checkWorkflows(filter: $filter, limit: 1) {
                  id
                }
              }
            }
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  }
`;

interface Props {
  trigger: React.ReactElement;
  graphId: string;
  graphVariant: string;
  checkWorkflowId: string;
  proposalRevisionId: string;
}

export function ProposalsCheckRerunButton({
  trigger,
  graphId,
  graphVariant,
  proposalRevisionId,
  checkWorkflowId,
}: Props) {
  const [workflowIdToForcePolling, setWorkflowIdToForcePolling] =
    useState<string>();
  const [rerunProposalCheck, { loading, error }] = useMutation(
    reRunCheckForProposalRevisionMutation,
    {
      variables: {
        variantRef: graphRefToString(useGraphRef()) || '',
        input: {
          revisionId: proposalRevisionId,
        },
        filter: {
          variants: [graphVariant],
          includeProposalChecks: true,
        },
      },
      update: (cache, { data }) => {
        const variant = data?.proposalByVariantRef;
        if (variant?.__typename === 'ProposalMutation') {
          const result = variant.reRunCheckForRevision;
          if (result.__typename === 'Proposal') {
            const maybeNewWorkflowId =
              result.backingVariant?.graph?.checkWorkflows?.at(0)?.id;
            // We should remove the proposalRevision id from the cache to hide
            // the rerun button after the mutation runs
            cache.evict({
              id: cache.identify({
                __typename: 'CheckWorkflow',
                id: maybeNewWorkflowId,
              }),
              fieldName: 'proposalRevision',
            });
            if (maybeNewWorkflowId !== checkWorkflowId) {
              cache.modify({
                id: cache.identify({
                  __typename: 'CheckWorkflow',
                  id: checkWorkflowId,
                }),
                fields: {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  'reruns({"limit":1})': (_cachedServices, { toReference }) => {
                    const newServiceRef = toReference(
                      { __typename: 'CheckWorkflow', id: maybeNewWorkflowId },
                      true,
                    );
                    return [newServiceRef];
                  },
                },
              });
            }
          }
        }
      },
      onCompleted: (data) => {
        const variant = data.proposalByVariantRef;
        if (variant?.__typename === 'ProposalMutation') {
          const result = variant.reRunCheckForRevision;
          if (result?.__typename === 'Proposal') {
            const maybeNewWorkflowId =
              result.backingVariant?.graph?.checkWorkflows?.at(0)?.id;
            if (maybeNewWorkflowId === checkWorkflowId) {
              setWorkflowIdToForcePolling(maybeNewWorkflowId);
            }
          } else if (
            result?.__typename === 'NotFoundError' ||
            result?.__typename === 'PermissionError' ||
            result?.__typename === 'ValidationError'
          ) {
            Sentry.captureMessage(result.message, { level: 'error' });
          }
        }
      },
    },
  );

  // The workflow id that is returned from the mutation is not guaranteed to be
  // the latest rerun. We should compare with the current workflow and poll for it
  // until we get the latest rerun. This only should run once or twice
  const [pollForLatestRerun, { data, loading: loadingRerunInfo }] =
    useLazyQuery<
      GraphQLTypes.ProposalCheckRerunBannerQuery,
      GraphQLTypes.ProposalCheckRerunBannerQueryVariables
    >(
      gql`
        query ProposalCheckRerunBannerQuery($graphId: ID!, $checkId: ID!) {
          service(id: $graphId) {
            id
            checkWorkflow(id: $checkId) {
              id
              reruns(limit: 1) {
                id
              }
            }
          }
        }
      `,
      {
        pollInterval: workflowIdToForcePolling ? 1_000 : 0,
        variables: {
          graphId,
          checkId: workflowIdToForcePolling as string,
        },
        fetchPolicy: 'network-only',
        context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      },
    );

  useEffect(() => {
    if (workflowIdToForcePolling && !loadingRerunInfo) {
      pollForLatestRerun();
    }

    const newestReruns = data?.service?.checkWorkflow?.reruns;
    if (isMinLengthArray(1, newestReruns)) {
      setWorkflowIdToForcePolling(undefined);
    }
  }, [
    data?.service?.checkWorkflow,
    workflowIdToForcePolling,
    pollForLatestRerun,
    loadingRerunInfo,
  ]);

  const reRunCallback = useCallback(() => {
    return rerunProposalCheck();
  }, [rerunProposalCheck]);

  return (
    <PermissionWarning graphId={graphId} graphPermissions="canCheckSchemas">
      <Popover autoFocus={false} placement="bottom-start" size="sm">
        <PopoverTrigger>{trigger}</PopoverTrigger>
        <PopoverContent>
          <PopoverHeader>
            Rerun this check
            <PopoverCloseButton />
          </PopoverHeader>
          <PopoverBody>
            <p>
              This will also update the status of your check in your schema
              proposal.
            </p>

            {error && <ErrorMessage className="my-4" />}
          </PopoverBody>
          <PopoverFooter>
            <Button
              variant="primary"
              onClick={reRunCallback}
              isLoading={loading}
              loadingText="Rerunning check"
            >
              Rerun check
            </Button>
          </PopoverFooter>
        </PopoverContent>
      </Popover>
    </PermissionWarning>
  );
}
